import type { ConditionOp, FieldDef } from './fields.js';
import type { ApprovalStage } from './submissions.js';

export type FormType =
  | 'trade_account'
  | 'credit_account'
  | 'supplier_account'
  | 'other';

/** The iQ entity a form's submissions create. */
export type IqEntity = 'lead' | 'customer' | 'supplier';

/**
 * Which iQ record a form creates: **supplier** applications become iQ suppliers,
 * trade / credit applications become **customers**, and everything else
 * (`other`) becomes a **lead** — each posted to its own iQ endpoint.
 */
export function iqEntityForType(type: FormType): IqEntity {
  if (type === 'supplier_account') return 'supplier';
  if (type === 'other') return 'lead';
  return 'customer';
}

/** Human label for an iQ entity (used in the builder mapping panel). */
export function iqEntityLabel(entity: IqEntity): string {
  return entity === 'customer' ? 'Customer' : entity === 'supplier' ? 'Supplier' : 'Lead';
}

/**
 * Default public origin for the forms renderer, used to build embed snippets and
 * the "Test form" link until a custom domain is configured (Settings → Integrations,
 * setting key `forms_base_url`).
 */
export const DEFAULT_FORMS_BASE_URL = 'https://jfh-forms.pages.dev';

/** Normalise a forms base URL: trim, add https://, drop any trailing slash. */
export function normaliseFormsBaseUrl(url: string | undefined | null): string {
  const v = (url ?? '').trim();
  if (!v) return DEFAULT_FORMS_BASE_URL;
  const withProto = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  return withProto.replace(/\/+$/, '');
}

export type FormStatus = 'draft' | 'published';

/** Where a form's submissions originate. */
export type FormSource = 'hosted' | 'wix';

/**
 * Legacy mapping: form field id → dotted path on the iQ Lead payload,
 * e.g. `{ f8: "lead.email", f2: "lead.companyName" }`.
 */
export type IqFieldMapping = Record<string, string>;

/** System-derived values available to the iQ mapping (not form fields). */
export type IqSystemKey =
  | 'submissionId'
  | 'submittedAt'
  | 'formType'
  | 'formName';

/**
 * One "when the driver field <op> X, send Y" rule for a derived value.
 * `op` defaults to `eq`; `equals` holds the comparison operand (unused for
 * `empty`/`not_empty`); `value` is what's sent when the rule matches.
 */
export interface IqDerivedRule {
  op?: ConditionOp;
  equals: string;
  value: string;
}

/** Where a mapped iQ value comes from. */
export type IqSource =
  | { type: 'field'; fieldId: string }
  | { type: 'const'; value: string; valueType?: 'string' | 'number' | 'boolean' }
  | { type: 'system'; key: IqSystemKey }
  /**
   * Value computed from another field via a lookup table — e.g. the iQ posting
   * map derived from the currency, so it never needs to appear on the form/card.
   */
  | { type: 'derived'; fieldId: string; rules: IqDerivedRule[]; fallback?: string }
  /**
   * A sub-field of the applicant-chosen primary contact (defaulting to the
   * first) on a `contact` field — e.g. the primary contact's email/phone/address
   * mapped onto customer-level iQ fields.
   */
  | { type: 'contact'; fieldId: string; subFieldId: string }
  /**
   * A freshly-generated website-account password, created at approval time and
   * sent to iQ (mapped to its password field) and to the applicant in the
   * approval email — never stored. Only resolves on the create that posts the
   * customer to iQ; absent (skipped) on a re-send.
   */
  | { type: 'password'; length?: number }
  /**
   * An extra field of the option selected in a lookup-backed field — e.g. the
   * **posting map** linked to the chosen currency. `fieldId` is the form field
   * (an iQ-list select); `field` names the lookup extra field to post.
   */
  | { type: 'lookup'; fieldId: string; field: string };

/** One iQ Lead payload assignment: a (possibly nested) target ← source. */
export interface IqMappingEntry {
  /** Dotted path into the lead payload, e.g. `lead.contact.email` or `items.0.code`. */
  target: string;
  source: IqSource;
}

/**
 * Org-wide iQ derivation: after a form's mappings build the payload, set
 * `targetPath` from the value already at `sourcePath` via a lookup table — e.g.
 * `DefaultCurrency.Code` (GBP) → `PostingMap.Code` (GBP). Applies to every form,
 * so the derived target need not exist on any form. Fills only when the target
 * is empty (explicit per-form mappings win).
 */
export interface IqDerivation {
  id: string;
  name?: string;
  sourcePath: string;
  targetPath: string;
  rules: IqDerivedRule[];
  fallback?: string;
}

export interface FormSettings {
  source?: FormSource;
  /** Stage-1 Teams channel (CS or Supplier). */
  channel?: string;
  /** Stage-2 Teams channel (credit → Accounts). */
  channel2?: string;
  consentText?: string;
  consentRequired?: boolean;
  /** Turnstile on public submission. Defaults to true. */
  turnstile?: boolean;
  /** Success message shown after submit. */
  success?: string;
  /** Optional redirect URL after submit. */
  redirect?: string;
  /** Legacy field→path mapping (superseded by iqMappings). */
  iqMapping?: IqFieldMapping;
  /** iQ Lead payload mapping entries (fields, constants, system values). */
  iqMappings?: IqMappingEntry[];
  /**
   * Credit only: mapping entries for the stage-2 PATCH sent to iQ when Accounts
   * approves the credit account (updates the existing customer record). When
   * empty, a legacy `{ creditStatus, creditLimit }` body is sent.
   */
  iqApprovalMappings?: IqMappingEntry[];
  /**
   * Post this form's submissions straight to iQ on submit, skipping the Teams
   * approval — for any entity (lead/customer/supplier). Lead forms always behave
   * this way; enabling it lets customer/supplier forms auto-create in iQ too.
   */
  postToIqOnSubmit?: boolean;
  /**
   * Links this form to a native Wix form. When the Wix webhook receives a
   * submission with this `formId`, it builds a submission from each field's
   * `wixKey` and runs the normal pipeline (mappings, entity, auto-post).
   */
  wixFormId?: string;
}

/** Resolve the iQ mapping entries, migrating any legacy `iqMapping`. */
export function resolveIqMappings(settings: FormSettings): IqMappingEntry[] {
  if (settings.iqMappings && settings.iqMappings.length) return settings.iqMappings;
  if (settings.iqMapping) {
    return Object.entries(settings.iqMapping).map(([fieldId, target]) => ({
      target,
      source: { type: 'field', fieldId },
    }));
  }
  return [];
}

/** Mapping entries for the stage-2 credit-approval PATCH (empty = legacy body). */
export function resolveIqApprovalMappings(settings: FormSettings): IqMappingEntry[] {
  return settings.iqApprovalMappings ?? [];
}

export interface Form {
  id: string;
  slug: string;
  name: string;
  type: FormType;
  status: FormStatus;
  currentVersionId: string | null;
  settings: FormSettings;
  /** Set when the form is archived (hidden from active lists + public). */
  archivedAt: string | null;
  /** Admin-only organising folder; null = top level. */
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the Forms list shows for each form beyond the form itself (F10 P4). */
export interface FormListStats {
  /** The live version number, or null when never published. */
  version: number | null;
  /** The draft has question changes the live version doesn't. */
  unpublishedChanges: boolean;
  /** Applications in the last 30 days, and ever. */
  last30: number;
  total: number;
  /** The last change to the form (its own audit trail), and who made it. */
  changedAt: string;
  changedBy: string | null;
}

/** Who decides a form's applications, in a few words (F10 P4). */
export function whoDecides(type: FormType, settings: FormSettings): string {
  if (type === 'trade_account') return 'Customer Service approves';
  if (type === 'credit_account') return 'Customer Service, then Accounts';
  if (type === 'supplier_account') return 'Supplier team approves';
  return resolveIqMappings(settings).length ? 'No approval, goes straight to iQ' : 'No approval';
}

/** An admin-only folder for organising forms. Nests via `parentId`. */
export interface FormFolder {
  id: string;
  /** null = top level. */
  parentId: string | null;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/** Deepest folder nesting allowed (a top-level folder is depth 1). */
export const MAX_FOLDER_DEPTH = 10;

/** An immutable snapshot of a form's field schema. */
export interface FormVersion {
  id: string;
  formId: string;
  version: number;
  schema: FieldDef[];
  publishedAt: string | null;
  publishedBy: string | null;
}

/** Fields that appear on the public form. */
export function customerFields(schema: FieldDef[]): FieldDef[] {
  return schema.filter((f) => f.audience === 'customer');
}

/** Fields completed by staff during approval (Teams card / portal). */
export function staffFields(schema: FieldDef[]): FieldDef[] {
  return schema.filter((f) => f.audience === 'staff');
}

/**
 * Split staff fields for a given approval stage into the ones that approver
 * edits and the ones to show as read-only context (already completed earlier in
 * the flow). On two-stage credit forms, Customer Service edits the `cs`/untagged
 * fields, and Accounts edits the `accounts`-tagged fields while seeing the rest
 * as context. Single-stage forms (trade/supplier) keep every staff field.
 */
export function stageStaffFields(
  schema: FieldDef[],
  stage: ApprovalStage,
): { editable: FieldDef[]; context: FieldDef[] } {
  const staff = staffFields(schema);
  if (stage === 'accounts') {
    return {
      editable: staff.filter((f) => f.staffStage === 'accounts'),
      context: staff.filter((f) => f.staffStage !== 'accounts'),
    };
  }
  if (stage === 'cs') {
    return { editable: staff.filter((f) => f.staffStage !== 'accounts'), context: [] };
  }
  return { editable: staff, context: [] };
}
